<template>
  <BaseModal
    :model-value="modelValue"
    title="Import recording"
    :title-logo="importIcon"
    width="640px"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="handleClose"
  >
    <div>
      <div class="flex flex-col gap-2">
        <h3 class="text-[16px] font-medium text-[#333] leading-6 tracking-[0.15px] m-0">
          Import Zoom recording
        </h3>
        <p class="text-[14px] text-[#979797] leading-5 tracking-[0.25px] m-0">
          Paste a Zoom recording or transcript link. We'll import it and generate a transcription and AI summary.
        </p>
      </div>

      <!-- Non-password error (show before Zoom link) -->
      <div v-if="error && errorType !== 'password'" class="mt-3 px-1">
        <Alert type="error">
          {{ error }}
        </Alert>
      </div>

      <div class="flex flex-col gap-1 mt-3 p-1">
        <label class="text-[14px] font-bold text-[#4a4a4a] leading-6">
          Zoom link
        </label>
        <input
          v-model="zoomLink"
          type="text"
          class="zoom-input"
          :class="{ 'zoom-input-error': errorType === 'link' }"
          :disabled="loading"
          placeholder="  https://zoom.us/rec/share/..."
        />
      </div>

      <div class="flex flex-col gap-1 mt-3 p-1">
        <label class="text-[14px] font-bold text-[#4a4a4a] leading-6">
          Enter recording passcode
        </label>
        <input
          ref="passcodeInputRef"
          v-model="passcode"
          type="text"
          class="zoom-input"
          :class="{ 'zoom-input-error': errorType === 'password' }"
          :disabled="loading"
        />
      </div>

      <!-- Password error (show after passcode input) -->
      <div v-if="error && errorType === 'password'" class="mt-1 px-1">
        <Alert type="error">
          {{ error }}
        </Alert>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <NewButton
          type="normal"
          color-type="red-stroke"
          :disabled="loading"
          @click="handleClose"
        >
          Cancel
        </NewButton>
        <NewButton
          type="normal"
          color-type="dark-blue"
          :disabled="!zoomLink.trim() || loading"
          :loading="loading"
          @click="handleImport"
        >
          Import Recording
        </NewButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import BaseModal from './BaseModal.vue';
import NewButton from '@/NewLibrary/Button.vue';
import Alert from '@/NewLibrary/Alert.vue';
import importIcon from '@/assets/encounterNotes/import.svg';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  errorType: {
    type: String,
    default: '', // 'password' | 'link' | ''
  },
});

const emit = defineEmits(['update:modelValue', 'import', 'close']);

const zoomLink = ref('');
const passcode = ref('');
const passcodeInputRef = ref(null);

// Focus passcode field when password error appears
watch(() => props.errorType, (val) => {
  if (val === 'password') {
    nextTick(() => {
      passcodeInputRef.value?.focus();
    });
  }
});

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
  zoomLink.value = '';
  passcode.value = '';
};

const handleImport = () => {
  emit('import', {
    zoomLink: zoomLink.value.trim(),
    passcode: passcode.value.trim(),
  });
};
</script>

<style scoped>
.zoom-input {
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #c9c9c9;
  border-radius: 4px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
  outline: none;
  transition: border-color 0.2s ease;
}

.zoom-input::placeholder {
  color: #999;
}

.zoom-input:focus {
  border-color: #004879;
}

.zoom-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.zoom-input-error {
  border-color: #e53935;
}

.zoom-input-error:focus {
  border-color: #e53935;
}
</style>
